import {
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  untracked,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BaseComponent } from 'src/app/shared';
import { customvalidations } from 'src/app/shared/models/customvalidations.validator';
import { SharedModule } from 'src/app/shared/shared.module';
import { InventoryItem } from '../../models/inventory/inventory-item';
import { InventoryPendingForm } from '../../models/inventory/inventory-request';
import {
  InventoryItemStatus,
  statusAfterEdit,
} from '../../models/inventory/inventory-item-status';
import {
  InventoryLimits,
  parseCost,
} from '../../models/inventory/inventory-limits';
import { createInventoryTranslationRequest } from '../../models/inventory/inventory-translation-request';
import { InventoryStateService } from '../../services/inventory/inventory-state.service';

@Component({
  selector: 'app-inventory-add-item',
  imports: [SharedModule],
  template: `
    <form [formGroup]="formGroup" class="tw-w-full">
      <div class="tw-w-full tw-flex tw-flex-wrap">
        <app-text-control
          formControlName="itemName"
          [formGroup]="formGroup"
          label="Item name"
          validationControl="Item name"
          [maxLength]="inventoryLimits.maxTextLength"
          [IsShowLimit]="false"
          ngDefaultControl
          class="tw-w-full md:tw-w-[20%]"
        ></app-text-control>

        <app-text-control
          formControlName="itemDescription"
          [formGroup]="formGroup"
          label="Item description"
          validationControl="Item description"
          textHint="Include any known brand, model, names or numbers, color, size design details and or design era."
          [maxLength]="inventoryLimits.maxTextLength"
          [IsShowLimit]="false"
          ngDefaultControl
          class="tw-w-full md:tw-w-[20%] long-hint-field"
        ></app-text-control>

        <app-select-control
          formControlName="quantity"
          [formGroup]="formGroup"
          label="Quantity"
          validationControl="Quantity"
          [options]="quantityOptions"
          [translationNeeded]="false"
          ngDefaultControl
          class="tw-w-full md:tw-w-[20%]"
        ></app-select-control>

        <app-text-control
          formControlName="cost"
          [formGroup]="formGroup"
          label="Cost per item"
          validationControl="Cost per item"
          mask="separator.2"
          thousandSeparator=","
          rejectNonNumericMessage="Error: Please enter a number."
          [maxLength]="inventoryLimits.maxFormattedCostLength"
          [IsShowLimit]="false"
          textHint="If unknown include best estimate for the per-item cost."
          ngDefaultControl
          class="tw-w-full md:tw-w-[20%] long-hint-field"
        ></app-text-control>

        <app-date-control
          formControlName="dateOfPurchase"
          [formGroup]="formGroup"
          label="Date of purchase"
          validationControl="Date of purchase"
          hintLabel="If unknown include best estimate. If Item gifted, include date received."
          [maxValue]="today"
          ngDefaultControl
          class="tw-w-full md:tw-w-[20%] date-of-purchase-field"
        ></app-date-control>
      </div>

      <div class="tw-w-full tw-flex tw-flex-wrap">
        <app-text-control
          formControlName="additionalItemDetails"
          [formGroup]="formGroup"
          label="Additional item details"
          textHint="Rare items such as antiques or collectibles will need specific details along with appropriate true age or release date (ex. Mid Century Modern)."
          [maxLength]="inventoryLimits.maxTextLength"
          [IsShowLimit]="false"
          ngDefaultControl
          class="tw-w-full md:tw-w-[20%] long-hint-field"
        ></app-text-control>

        <app-select-control
          formControlName="preLossCondition"
          [formGroup]="formGroup"
          label="Pre-loss condition"
          [options]="preLossConditionOptions"
          ngDefaultControl
          class="tw-w-full md:tw-w-[20%]"
        ></app-select-control>

        <app-text-control
          formControlName="wherePurchased"
          [formGroup]="formGroup"
          label="Where purchased"
          [maxLength]="inventoryLimits.maxTextLength"
          [IsShowLimit]="false"
          ngDefaultControl
          class="tw-w-full md:tw-w-[20%]"
        ></app-text-control>

        <app-select-control
          formControlName="methodOfPayment"
          [formGroup]="formGroup"
          label="Method of payment"
          validationControl="Method of payment"
          [options]="methodOfPaymentOptions"
          ngDefaultControl
          class="tw-w-full md:tw-w-[20%]"
        ></app-select-control>

        <app-select-control
          formControlName="receiptOrInvoiceIncluded"
          [formGroup]="formGroup"
          label="Receipt or invoice included?"
          [options]="receiptOptions"
          ngDefaultControl
          class="tw-w-full md:tw-w-[20%]"
        ></app-select-control>
      </div>

      @if (formGroup.controls['methodOfPayment'].value === 'Other') {
        <div class="tw-w-full tw-flex tw-flex-wrap">
          <app-text-control
            formControlName="specifyMethodOfPayment"
            [formGroup]="formGroup"
            label="Specify method of payment"
            [maxLength]="inventoryLimits.maxTextLength"
            [IsShowLimit]="false"
            ngDefaultControl
            class="tw-w-full"
          ></app-text-control>
        </div>
      }

      @if (formGroup.controls['receiptOrInvoiceIncluded'].value === 'No') {
        <div class="tw-w-full tw-flex tw-flex-wrap">
          <app-text-control
            formControlName="reasonForNoDocumentation"
            [formGroup]="formGroup"
            label="Reason for not supporting documentation"
            [maxLength]="inventoryLimits.maxTextLength"
            [IsShowLimit]="false"
            ngDefaultControl
            class="tw-w-full"
          ></app-text-control>
        </div>
      }

      @if (noChangesDetected) {
        <div class="tw-w-full">
          <app-toast type="Info">
            {{
              'No changes have been made.' | translation: translationRequest
            }}
          </app-toast>
        </div>
      }

      <app-action-row class="tw-w-full app-pt2">
        <app-button
          right2
          emphasis="High"
          [label]="isEditMode() ? 'Save changes' : 'Add'"
          [disabled]="readOnly()"
          (click)="isEditMode() ? saveChanges() : addItem()"
        ></app-button>
        <app-button
          right1
          emphasis="Low"
          [label]="isEditMode() ? 'Cancel edit item' : 'Cancel'"
          [disabled]="!hasFormInput"
          (click)="openCancelDialog()"
        ></app-button>
      </app-action-row>
    </form>

    <ng-template #cancelTemplate>
      <app-dialog
        [title]="isEditMode() ? 'Cancel edit item' : 'Cancel add item'"
        [ariaLabel]="
          isEditMode()
            ? 'Cancel edit item confirmation dialog'
            : 'Cancel add item confirmation dialog'
        "
        [confirmLabel]="'Confirm'"
        [cancelLabel]="'Back'"
        [closeOnConfirm]="true"
        [showHeader]="true"
        [width]="500"
        (confirmEvent)="cancelConfirmed($event)"
      >
        {{
          'Canceling will discard any unsaved data. Press Confirm to cancel or Back to return to the form.'
            | translation: translationRequest
        }}
      </app-dialog>
    </ng-template>
  `,
  styles: [
    `
      .date-of-purchase-field {
        margin-bottom: 1.75rem;
      }

      .date-of-purchase-field ::ng-deep .mat-mdc-form-field-subscript-wrapper {
        min-height: 2.75rem;
      }

      .long-hint-field ::ng-deep .mat-mdc-form-field-subscript-wrapper {
        height: auto;
      }

      .long-hint-field ::ng-deep .mat-mdc-form-field-bottom-align::before {
        display: none;
      }

      .long-hint-field ::ng-deep .mat-mdc-form-field-hint-wrapper {
        position: static;
      }
    `,
  ],
})
export class InventoryAddItemComponent extends BaseComponent implements OnInit {
  @ViewChild('cancelTemplate')
  cancelTemplate: TemplateRef<any>;

  today = new Date();
  hasFormInput = false;
  noChangesDetected = false;

  readonly isEditMode = this.inventoryState.isEditing;
  readonly readOnly = computed(() => this.inventoryState.claimClosed());

  private patchedItemId: number | null = null;

  translationRequest = createInventoryTranslationRequest();
  readonly inventoryLimits = InventoryLimits;

  quantityOptions = Array.from(
    { length: InventoryLimits.maxQuantity - InventoryLimits.minQuantity + 1 },
    (_, index) => {
      const quantity = String(index + InventoryLimits.minQuantity);
      return { label: quantity, value: quantity };
    },
  );

  preLossConditionOptions = [
    { label: 'Very good', value: 'Very good' },
    { label: 'Good', value: 'Good' },
    { label: 'Fair', value: 'Fair' },
  ];

  methodOfPaymentOptions = [
    { label: 'Cash', value: 'Cash' },
    { label: 'Check', value: 'Check' },
    { label: 'Credit/Debit card', value: 'Credit/Debit card' },
    { label: 'Gift card', value: 'Gift card' },
    { label: 'ACH transfer', value: 'ACH transfer' },
    {
      label: 'Digital (PayPal, Venmo, etc.)',
      value: 'Digital (PayPal, Venmo, etc.)',
    },
    { label: 'Other', value: 'Other' },
  ];

  receiptOptions = [
    { label: 'Yes', value: 'Yes' },
    { label: 'No', value: 'No' },
  ];

  formGroup = this.fb.group({
    itemName: [
      '',
      [
        Validators.required,
        Validators.maxLength(InventoryLimits.maxTextLength),
      ],
    ],
    itemDescription: [
      '',
      [
        Validators.required,
        Validators.maxLength(InventoryLimits.maxTextLength),
      ],
    ],
    quantity: ['', [Validators.required]],
    cost: [
      '',
      [
        Validators.required,
        customvalidations.IsDecimal('Error: Please enter a number.'),
        customvalidations.MinDecimalValue(
          InventoryLimits.minCost,
          'Error: Cost per item must be greater than 0',
        ),
        customvalidations.MaxDecimalDigits(
          InventoryLimits.maxCostIntegerDigits,
          InventoryLimits.maxCostDecimalDigits,
          'Error: Cost per item is too large',
        ),
      ],
    ],
    dateOfPurchase: [
      '',
      [
        Validators.required,
        customvalidations.dateInFuture(
          'Error: The date of purchase cannot be a future date.',
        ),
      ],
    ],
    additionalItemDetails: [
      '',
      [Validators.maxLength(InventoryLimits.maxTextLength)],
    ],
    preLossCondition: [''],
    wherePurchased: [
      '',
      [Validators.maxLength(InventoryLimits.maxTextLength)],
    ],
    methodOfPayment: ['', [Validators.required]],
    specifyMethodOfPayment: [
      '',
      [Validators.maxLength(InventoryLimits.maxTextLength)],
    ],
    receiptOrInvoiceIncluded: [''],
    reasonForNoDocumentation: [
      '',
      [Validators.maxLength(InventoryLimits.maxTextLength)],
    ],
  });

  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    private inventoryState: InventoryStateService,
  ) {
    super();

    effect(() => {
      const itemId = this.inventoryState.editingItemId();
      if (itemId === this.patchedItemId) {
        return;
      }

      this.patchedItemId = itemId;
      const item = untracked(() => this.inventoryState.editingItem());

      if (item) {
        this.patchFrom(item);
      } else {
        this.resetForm();
      }
    });

    effect(() => this.applyReadOnly(this.readOnly()));

    effect(() => {
      const resetCount = this.inventoryState.formResetRequests();
      const pending = this.inventoryState.loadedPendingForm();
      const submitted = this.inventoryState.hasSubmitted();
      untracked(() => {
        this.resetForm();
        if (resetCount === 0 && pending && !submitted) {
          this.restorePendingForm(pending);
        }
      });
    });
  }

  ngOnInit() {
    this.subs.sink = this.formGroup.valueChanges.subscribe(() => {
      this.noChangesDetected = false;
      this.hasFormInput = this.formHasInput();
      this.inventoryState.setFormDirty(this.hasFormInput);
      this.syncParameterSection();
    });
  }

  addItem() {
    if (this.readOnly()) {
      return;
    }
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }

    const values = this.formGroup.getRawValue();
    const item: InventoryItem = {
      id: this.inventoryState.nextItemId(),
      itemName: values.itemName,
      itemDescription: values.itemDescription,
      quantity: Number(values.quantity),
      cost: parseCost(values.cost),
      dateOfPurchase: new Date(values.dateOfPurchase),
      additionalItemDetails: values.additionalItemDetails || undefined,
      preLossCondition: values.preLossCondition || undefined,
      wherePurchased: values.wherePurchased || undefined,
      methodOfPayment: values.methodOfPayment || undefined,
      specifyMethodOfPayment: values.specifyMethodOfPayment || undefined,
      receiptOrInvoiceIncluded: values.receiptOrInvoiceIncluded || undefined,
      reasonForNoDocumentation: values.reasonForNoDocumentation || undefined,
      status: InventoryItemStatus.new,
      attachments: [],
      attachmentCount: 0,
      dateUploaded: new Date(),
    };

    this.inventoryState.addItem(item);
    this.resetForm();
  }

  saveChanges() {
    const editingItem = this.inventoryState.editingItem();
    if (!editingItem || this.readOnly()) {
      return;
    }

    if (!this.hasEdits(editingItem)) {
      this.noChangesDetected = true;
      return;
    }

    this.noChangesDetected = false;

    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }

    const values = this.formGroup.getRawValue();
    const updatedItem: InventoryItem = {
      ...editingItem,
      itemName: values.itemName,
      itemDescription: values.itemDescription,
      quantity: Number(values.quantity),
      cost: parseCost(values.cost),
      dateOfPurchase: new Date(values.dateOfPurchase),
      additionalItemDetails: values.additionalItemDetails || undefined,
      preLossCondition: values.preLossCondition || undefined,
      wherePurchased: values.wherePurchased || undefined,
      methodOfPayment: values.methodOfPayment || undefined,
      specifyMethodOfPayment: values.specifyMethodOfPayment || undefined,
      receiptOrInvoiceIncluded: values.receiptOrInvoiceIncluded || undefined,
      reasonForNoDocumentation: values.reasonForNoDocumentation || undefined,
      status: statusAfterEdit(editingItem.status),
    };

    this.inventoryState.updateItem(updatedItem);
  }

  openCancelDialog() {
    if (!this.hasFormInput) {
      return;
    }
    this.dialog.open(this.cancelTemplate);
  }

  cancelConfirmed(event: string) {
    if (event !== 'confirm') {
      return;
    }

    if (this.isEditMode()) {
      this.inventoryState.stopEditing();
    } else {
      this.resetForm();
    }
  }

  private syncParameterSection(): void {
    const hasInput = this.formHasInput();
    this.hasFormInput = hasInput;

    if (this.isEditMode()) {
      this.inventoryState.setFormDirty(hasInput);
      this.inventoryState.setParameterDraft(null, false);
      return;
    }

    const valid = hasInput && this.formGroup.valid;
    this.inventoryState.setFormDirty(hasInput);
    this.inventoryState.setParameterDraft(
      valid ? this.toPendingForm() : null,
      valid,
    );
  }

  private toPendingForm(): InventoryPendingForm {
    const values = this.formGroup.getRawValue();
    const date = values.dateOfPurchase
      ? new Date(values.dateOfPurchase as string)
      : null;

    return {
      itemName: values.itemName ?? '',
      itemDescription: values.itemDescription ?? '',
      quantity: values.quantity ?? '',
      cost: values.cost ?? '',
      dateOfPurchase:
        date && !isNaN(date.getTime()) ? date.toISOString() : '',
      additionalItemDetails: values.additionalItemDetails ?? '',
      preLossCondition: values.preLossCondition ?? '',
      wherePurchased: values.wherePurchased ?? '',
      methodOfPayment: values.methodOfPayment ?? '',
      specifyMethodOfPayment: values.specifyMethodOfPayment ?? '',
      receiptOrInvoiceIncluded: values.receiptOrInvoiceIncluded ?? '',
      reasonForNoDocumentation: values.reasonForNoDocumentation ?? '',
    };
  }

  private restorePendingForm(pending: InventoryPendingForm): void {
    const date = pending.dateOfPurchase
      ? new Date(pending.dateOfPurchase)
      : null;

    this.formGroup.patchValue(
      {
        itemName: pending.itemName ?? '',
        itemDescription: pending.itemDescription ?? '',
        quantity: pending.quantity ?? '',
        cost: pending.cost ?? '',
        dateOfPurchase: (date && !isNaN(date.getTime()) ? date : '') as any,
        additionalItemDetails: pending.additionalItemDetails ?? '',
        preLossCondition: pending.preLossCondition ?? '',
        wherePurchased: pending.wherePurchased ?? '',
        methodOfPayment: pending.methodOfPayment ?? '',
        specifyMethodOfPayment: pending.specifyMethodOfPayment ?? '',
        receiptOrInvoiceIncluded: pending.receiptOrInvoiceIncluded ?? '',
        reasonForNoDocumentation: pending.reasonForNoDocumentation ?? '',
      },
      { emitEvent: false },
    );

    this.hasFormInput = true;
    this.inventoryState.setFormDirty(false);
    this.inventoryState.setParameterDraft(pending, this.formGroup.valid);
  }

  private patchFrom(item: InventoryItem) {
    this.formGroup.patchValue({
      itemName: item.itemName,
      itemDescription: item.itemDescription,
      quantity: String(item.quantity),
      cost: String(item.cost),
      dateOfPurchase: item.dateOfPurchase as any,
      additionalItemDetails: item.additionalItemDetails ?? '',
      preLossCondition: item.preLossCondition ?? '',
      wherePurchased: item.wherePurchased ?? '',
      methodOfPayment: item.methodOfPayment ?? '',
      specifyMethodOfPayment: item.specifyMethodOfPayment ?? '',
      receiptOrInvoiceIncluded: item.receiptOrInvoiceIncluded ?? '',
      reasonForNoDocumentation: item.reasonForNoDocumentation ?? '',
    });
    this.hasFormInput = this.formHasInput();
    this.inventoryState.setFormDirty(this.hasFormInput);
  }

  private resetForm() {
    this.formGroup.reset();
    this.hasFormInput = false;
    this.noChangesDetected = false;
    this.inventoryState.setFormDirty(false);
    this.inventoryState.setParameterDraft(null, false);
    this.applyReadOnly(this.readOnly());
  }

  private applyReadOnly(readOnly: boolean) {
    if (readOnly) {
      this.formGroup.disable({ emitEvent: false });
    } else {
      this.formGroup.enable({ emitEvent: false });
    }
  }

  private hasEdits(item: InventoryItem): boolean {
    return (
      JSON.stringify(this.fieldsFromForm()) !==
      JSON.stringify(this.fieldsFromItem(item))
    );
  }

  private fieldsFromForm() {
    const values = this.formGroup.getRawValue();
    return this.comparableFields(values);
  }

  private fieldsFromItem(item: InventoryItem) {
    return this.comparableFields({
      itemName: item.itemName,
      itemDescription: item.itemDescription,
      quantity: item.quantity,
      cost: item.cost,
      dateOfPurchase: item.dateOfPurchase,
      additionalItemDetails: item.additionalItemDetails,
      preLossCondition: item.preLossCondition,
      wherePurchased: item.wherePurchased,
      methodOfPayment: item.methodOfPayment,
      specifyMethodOfPayment: item.specifyMethodOfPayment,
      receiptOrInvoiceIncluded: item.receiptOrInvoiceIncluded,
      reasonForNoDocumentation: item.reasonForNoDocumentation,
    });
  }

  private comparableFields(values: {
    itemName?: string | null;
    itemDescription?: string | null;
    quantity?: string | number | null;
    cost?: string | number | null;
    dateOfPurchase?: string | Date | null;
    additionalItemDetails?: string | null;
    preLossCondition?: string | null;
    wherePurchased?: string | null;
    methodOfPayment?: string | null;
    specifyMethodOfPayment?: string | null;
    receiptOrInvoiceIncluded?: string | null;
    reasonForNoDocumentation?: string | null;
  }) {
    return {
      itemName: values.itemName ?? '',
      itemDescription: values.itemDescription ?? '',
      quantity: Number(values.quantity),
      costCents: Math.round(parseCost(values.cost) * 100),
      dateOfPurchase: this.calendarDateKey(values.dateOfPurchase),
      additionalItemDetails: values.additionalItemDetails ?? '',
      preLossCondition: values.preLossCondition ?? '',
      wherePurchased: values.wherePurchased ?? '',
      methodOfPayment: values.methodOfPayment ?? '',
      specifyMethodOfPayment: values.specifyMethodOfPayment ?? '',
      receiptOrInvoiceIncluded: values.receiptOrInvoiceIncluded ?? '',
      reasonForNoDocumentation: values.reasonForNoDocumentation ?? '',
    };
  }

  private calendarDateKey(value: string | Date | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return '';
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  private formHasInput(): boolean {
    const values = this.formGroup.getRawValue();
    return Object.values(values).some((value) => {
      if (value === null || value === undefined || value === '') {
        return false;
      }
      if (typeof value === 'object' && 'getTime' in (value as object)) {
        return !isNaN((value as Date).getTime());
      }
      return true;
    });
  }
}
